import importDialogTemplate from './importDialog.html';
import './importDialog.scss';

/*******************************************************************************
 * @ngdoc    directive
 * @name     csm.books.InitialSetup.dialogs.directive:importDialog
 * @requires csm.ui.importForm
 * @requires ngFileUpload
 * @scope
 *
 * @param {string[]} columnHeaders
 * CSV input file column header names. Displayed across the top before the file
 * input button.
 *
 * @param {string} helpUrl
 * Help dialog content URL shown on click of `Help` button.
 *
 * @param {string} itemsLabel
 * Label to display for the total items imported (plural).
 *
 * @param {expr} onSave
 * Expression to execute following successful import and `Ok` button is clicked.
 * `$items` is passed as array of imported rows.
 *
 * @param {string} title
 * Dialog title.
 *
 * @param {string} serviceUrl
 * Service URL on server. Takes no route parameters.
 *
 * @param {boolean} showResultsOnError
 * Show results table when there are errors flag.
 *
 * @description
 * A modal dialog for importing setup information. `csm.ui.importForm.importForm`
 * is used to take input of the file to upload and report the results. Uses the
 * `using-ajax` option to post the file to a service using `ngFileUpload`.
 *
 * This directive adds `setData(items)` to the `window.scopeInterface` created by
 * `importForm`. When the import is complete, `serviceUrl` calls this method
 * passing the array of imported data in `items`.
 *******************************************************************************/
/* @ngInject */ export function importDialogDirective(ClickDialogDirective) {
  return new ClickDialogDirective({
    template: importDialogTemplate,
    scope: {
      columnHeaders: '<',
      helpUrl: '<',
      itemsLabel: '@',
      onSave: '&',
      serviceUrl: '<',
      showResultsOnError: '<',
      title: '@'
    },
    backdrop: 'static',
    openedClass: 'import-form-modal-open',
    windowClass: 'import-dialog',
    controller: controller
  });
};

/*******************************************************************************
 * controller
 *******************************************************************************/
/* @ngInject */ function controller(
  $injector,
  $scope,
  $uibModalInstance,
  HelpDialog
) {
  var $ctrl = this;
  var $svcs = { $ctrl, $scope, $uibModalInstance};
  Object.assign( $svcs, { $svcs });
  $scope.scopeInterface = $injector.invoke(scopeInterface, null, $svcs);

  $ctrl.$onInit = function() {
    Object.assign($ctrl, {
      importedItems: undefined,
      importComplete: false,
      importStarted: false,

      save: $injector.invoke(save, null, $svcs),
      showHelp: () => HelpDialog.show($ctrl.helpUrl)
    });
  };
}

/***************************************************************************
 * $ctrl.save */
/**
 * Save button handler. Calls `onSave` with `$items` and closes dialog.
 ***************************************************************************/
/* @ngInject */ function save($ctrl, $uibModalInstance) {
  return function () {
    $ctrl.onSave({ $items: $ctrl.importedItems });
    $uibModalInstance.close();
  };
}

/*******************************************************************************
 * $ctrl.scopeInterface */
/**
 * Methods added to `window.scopeInterface` and called from IFRAME.
 *
 * Note: `scopeInterface` calls from the IFRAME are outside of the application
 *       watch cycles and will not trigger a digest cycle. `$timeout` is used to
 *       fire a digest cycle to update parent page views with scope changes.
 *******************************************************************************/
/* @ngInject */ function scopeInterface($ctrl, $injector, $svcs) {
  return Object.assign(($svcs.$self = {}), {
    saveData: $injector.invoke(saveData, null, $svcs)
  });

  /*************************************************************************
   * $ctrs.scopeInterface.setData */
  /**
   * Saves items returned from importer and sets `importComplete` flag.
   *
   * @param {Array} items  Imported items.
   *************************************************************************/
  /* @ngInject */ function saveData($ctrl, $timeout) {
    return function (items) {
      $timeout(function () {
        $ctrl.importedItems = items;
        $ctrl.importComplete = true;
      });
    };
  }
}
